/**
 * Werkzeugleiste am unteren Rand.
 *
 * Alles, was während der Prüfung gebraucht wird, liegt im Daumenbereich:
 * Fortschritt, Sprung zum nächsten unbeurteilten Punkt, Foto, Abschluss.
 * Die obere Bildschirmhälfte erreicht man einhändig nicht.
 */

import { useRef } from 'react'
import type { Fortschritt } from '../api/typen'

type Eigenschaften = {
  fortschritt: Fortschritt
  wartend: number
  abgeschlossen: boolean
  beimSprung: () => void
  beimFoto: (datei: File) => void
  beimAbschluss: () => void
  beimZurueck: () => void
}

export function Werkzeugleiste({
  fortschritt,
  wartend,
  abgeschlossen,
  beimSprung,
  beimFoto,
  beimAbschluss,
  beimZurueck,
}: Eigenschaften) {
  const kamera = useRef<HTMLInputElement>(null)

  const anteil =
    fortschritt.punkte_gesamt > 0
      ? Math.round((fortschritt.punkte_beurteilt / fortschritt.punkte_gesamt) * 100)
      : 0

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-30 border-t-2 border-slate-300 bg-white"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Fortschrittsbalken: die Zahl steht daneben, der Balken allein wäre
          bei Sonne kaum zu erkennen. */}
      <div
        className="h-2 w-full bg-slate-200"
        role="progressbar"
        aria-valuenow={fortschritt.punkte_beurteilt}
        aria-valuemin={0}
        aria-valuemax={fortschritt.punkte_gesamt}
        aria-label="Fortschritt der Prüfung"
      >
        <div
          className={`h-full transition-[width] ${
            fortschritt.abschluss_moeglich ? 'bg-stufe-ob' : 'bg-marke-600'
          }`}
          style={{ width: `${anteil}%` }}
        />
      </div>

      <div className="mx-auto flex max-w-5xl items-stretch gap-2 px-3 py-2">
        <button
          type="button"
          onClick={beimZurueck}
          className="knopf-still px-3"
          aria-label="Zurück zum Spielplatz"
          title="Zurück zum Spielplatz"
        >
          ←
        </button>

        {/* Zwei knappe Zeilen statt einer langen: auf 375 px Breite bleibt neben
            den drei Knöpfen kaum Platz, und eine umbrechende Zahl ist im
            Sonnenlicht nicht zu lesen. */}
        <div className="flex min-w-0 flex-1 flex-col justify-center leading-tight">
          <div className="whitespace-nowrap">
            <span className="text-xl font-bold tabular-nums">
              {fortschritt.punkte_beurteilt}
            </span>
            <span className="text-slate-600">/{fortschritt.punkte_gesamt}</span>
          </div>
          <div className="truncate text-sm">
            {fortschritt.maengel_gesamt > 0 && (
              <span className="font-semibold text-stufe-2">
                {fortschritt.maengel_gesamt}{' '}
                {fortschritt.maengel_gesamt === 1 ? 'Mangel' : 'Mängel'}
              </span>
            )}
            {fortschritt.maengel_gesamt > 0 && <span className="text-slate-400"> · </span>}
            <span className="text-slate-600">
              {abgeschlossen
                ? 'abgeschlossen'
                : fortschritt.punkte_offen > 0
                  ? `${fortschritt.punkte_offen} offen`
                  : 'alles beurteilt'}
              {wartend > 0 && ` · ${wartend} wartend`}
            </span>
          </div>
        </div>

        {!abgeschlossen && (
          <>
            <input
              ref={kamera}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const datei = e.target.files?.[0]
                e.target.value = ''
                if (datei) beimFoto(datei)
              }}
            />
            <button
              type="button"
              onClick={() => kamera.current?.click()}
              className="knopf-zweit px-3"
              aria-label="Foto zum Platz aufnehmen"
              title="Foto zum Platz aufnehmen"
            >
              Foto
            </button>

            {fortschritt.punkte_offen > 0 ? (
              <button
                type="button"
                onClick={beimSprung}
                className="knopf-haupt px-4"
                disabled={!fortschritt.naechster_offener_id}
              >
                Nächster
              </button>
            ) : (
              <button type="button" onClick={beimAbschluss} className="knopf-haupt px-4">
                Abschließen
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
